import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * What a call attempt did, and what the user should be told about it.
 * `outcome` drives the styling; the two strings are the message itself.
 */
export interface CallNotice {
  outcome: 'dialling' | 'no-number' | 'disabled' | 'not-installed';
  title: string;
  detail: string;
  /** The number handed to the dialler, formatted for display. */
  number?: string;
  /** Who is being called, for the message. */
  name?: string;
}

/**
 * Click-to-call through 3CX.
 *
 * How this actually works: 3CX registers itself on the desktop as the handler
 * for `tel:` links, so "placing a call" from a web page means navigating to a
 * `tel:` URL and letting the operating system hand it to 3CX. There is no API
 * to ask the browser whether a handler is installed — the navigation either
 * reaches 3CX or silently does nothing.
 *
 * That is the whole reason the notice below exists. Because a failed hand-off
 * is invisible, the user is TOLD what was attempted and what to check if their
 * phone does not ring. Guessing "the call is connecting" would be a lie the
 * page cannot verify.
 *
 * The switch and the scheme are configurable so an operator can turn calling
 * off, or point it at a different dialler, without a code change.
 */
@Injectable({ providedIn: 'root' })
export class PhoneCallService {
  /** Defaults; an environment may override any of them. */
  private readonly defaults = {
    enabled: true,
    scheme: 'tel',
    provider: '3CX',
  };

  get config(): { enabled: boolean; scheme: string; provider: string } {
    const configured = (environment as any)?.calling || {};
    return { ...this.defaults, ...configured };
  }

  get provider(): string {
    return this.config.provider;
  }

  get enabled(): boolean {
    return this.config.enabled !== false;
  }

  /**
   * Strips the punctuation people type into phone fields, keeping a leading +.
   * "+31 (0)6 8738 2356" -> "+31068738 2356" would not dial, so spaces,
   * brackets, dots and dashes all go.
   */
  normalise(raw: string | null | undefined): string {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const plus = text.startsWith('+') ? '+' : '';
    return plus + text.replace(/[^\d]/g, '');
  }

  /** True when there is something dialable on this record. */
  canCall(contact: { phone?: string | null; alternatePhone?: string | null } | null): boolean {
    if (!contact) return false;
    return !!(this.normalise(contact.phone) || this.normalise(contact.alternatePhone));
  }

  /**
   * Places the call and returns what to show the user.
   *
   * Nothing is thrown: a call that cannot be placed is a message, not an error.
   */
  call(rawNumber: string | null | undefined, name?: string): CallNotice {
    const number = this.normalise(rawNumber);
    const who = (name || '').trim();
    const provider = this.provider;

    if (!this.enabled) {
      return {
        outcome: 'disabled',
        name: who,
        title: `${provider} calling is switched off`,
        detail:
          `Click-to-call is not enabled for this portal, so the number cannot be dialled from here. ` +
          `Ask your administrator to enable ${provider} calling, or dial the number manually.`,
      };
    }

    if (!number) {
      return {
        outcome: 'no-number',
        name: who,
        title: who ? `No phone number for ${who}` : 'No phone number on record',
        detail:
          'This contact has no telephone number saved on MPS, so there is nothing to dial. ' +
          'Use the email address instead, or ask them to add a number to their profile.',
      };
    }

    // Hand off to the desktop dialler. An anchor click is used rather than
    // location.href so the current page is never navigated away from if no
    // handler is registered.
    try {
      const link = document.createElement('a');
      link.href = `${this.config.scheme}:${number}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // The hand-off is best-effort; the notice below already tells the user
      // what to check, and it is the same advice either way.
    }

    return {
      outcome: 'dialling',
      number,
      name: who,
      title: who ? `Calling ${who} on ${number}` : `Calling ${number}`,
      detail: `Handing the call to ${provider}…`,
    };
  }

  /**
   * Did the operating system actually hand the call to a dialler?
   *
   * A browser cannot be asked whether an application is installed — there is no
   * API for it, by design. What CAN be observed is the side effect: when the OS
   * passes a `tel:` link to a desktop app, that app takes focus and this page
   * loses it. No focus change within the window below means nothing accepted
   * the link, which in practice means 3CX is not installed or not registered as
   * the handler for telephone links.
   *
   * It is a heuristic, not a guarantee, so the message it produces says what
   * was observed and what to check — never "3CX is not installed" as a fact.
   *
   * @returns null when the call was handed off, or the notice to show when it
   *          was not.
   */
  awaitHandoff(number: string, name?: string, settleMs = 2500): Promise<CallNotice | null> {
    const provider = this.provider;
    const who = (name || '').trim();

    return new Promise((resolve) => {
      // Deliberately NOT "did a blur ever happen".
      //
      // Chrome asks "localhost wants to open this application?" before handing
      // a tel: link over, and that dialog takes focus itself — so a blur fires
      // whether or not anything is installed, and whether or not the user then
      // cancels. Treating the first blur as success reported a call placed to
      // someone who has no dialler at all.
      //
      // What separates the two cases is where focus ends up once the dust
      // settles: if an application really opened, this page is still in the
      // background; if the prompt was dismissed or nothing could handle the
      // link, focus has come back here.
      setTimeout(() => {
        const stillInBackground =
          document.visibilityState === 'hidden' ||
          (typeof document.hasFocus === 'function' && !document.hasFocus());

        if (stillInBackground) {
          resolve(null);
          return;
        }

        resolve({
          outcome: 'not-installed',
          number,
          name: who,
          // Stated as an observation, not a verdict. A cold start of the
          // dialler can outlast this window, so the page can be wrong here —
          // and telling someone their call failed when it is ringing would be
          // worse than leaving room for it.
          title: `${provider} may not have picked up this call`,
          detail:
            `This page did not see ${provider} open, so ${who || 'this contact'} may not have been dialled. ` +
            `If your phone is already ringing, ignore this. Otherwise ${provider} is probably not installed, ` +
            `not running, or the prompt to open it was dismissed.`,
        });
      }, settleMs);
    });
  }

  /** What to do about it — shown with a not-installed notice. */
  get installSteps(): string[] {
    const provider = this.provider;
    return [
      `Check that the ${provider} desktop app is installed and signed in.`,
      `Start ${provider} and leave it running, then try the call again.`,
      `If it is running, set ${provider} as the handler for telephone (tel:) links in your browser settings.`,
      `Still not working? Dial the number by hand — it is shown above.`,
    ];
  }
}
